import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  Award,
  Bot,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  History,
  LineChart,
  Rocket,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from 'lucide-react';
import { api } from '../../api/client';
import StatCard from '../../components/StatCard';
import EmptyState from '../../components/EmptyState';
import { SkeletonHero, SkeletonGrid, SkeletonCard } from '../../components/Skeleton';
import { formatRelative } from '../../lib/format';

const ACTION_META = {
  domain_added: { label: 'Domain added', icon: Sparkles, tone: 'brand' },
  domain_verified: { label: 'Domain verified', icon: ShieldCheck, tone: 'success' },
  rewrite: { label: 'Page rewritten', icon: FileText, tone: 'brand' },
  bulk_rewrite: { label: 'Bulk rewrite', icon: FileText, tone: 'brand' },
  audit_run: { label: 'Audit run', icon: ClipboardCheck, tone: 'brand' },
  bot_detected: { label: 'Bot detected', icon: Bot, tone: 'warn' },
  referral_tracked: { label: 'Referral tracked', icon: LineChart, tone: 'success' },
  bot_access_updated: { label: 'Bot access updated', icon: SettingsIcon, tone: 'muted' },
  llms_txt_updated: { label: 'llms.txt updated', icon: FileText, tone: 'muted' },
  settings_updated: { label: 'Settings updated', icon: SettingsIcon, tone: 'muted' },
};

const TONE_CLASSES = {
  brand: 'bg-brand-50 text-brand-700',
  success: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-amber-50 text-amber-700',
  muted: 'bg-gray-100 text-ink-700',
};

function ActivityFeed({ domainId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['user-activity'],
    queryFn: () => api.get('/api/me/activity').then((r) => r.data.activity),
  });

  if (isLoading) return null;
  const items = (data || [])
    .filter((a) => !domainId || a.domain_id?.toString() === domainId.toString())
    .slice(0, 8);

  if (!items.length) return null;

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <History className="w-4 h-4" />
          </span>
          Recent activity
        </div>
        <span className="text-xs text-ink-400">Last 8 events</span>
      </div>
      <ul className="divide-y divide-gray-50 text-sm">
        {items.map((a) => {
          const meta = ACTION_META[a.action] || {
            label: a.action,
            icon: Activity,
            tone: 'muted',
          };
          const Icon = meta.icon;
          return (
            <li
              key={a._id}
              className="py-3 flex items-start justify-between gap-3 first:pt-0 last:pb-0"
            >
              <div className="min-w-0 flex items-center gap-3">
                <span
                  className={`flex-none h-8 w-8 rounded-lg flex items-center justify-center ${TONE_CLASSES[meta.tone]}`}
                >
                  <Icon className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-ink-900 font-medium">{meta.label}</div>
                  {(a.details?.url || a.details?.domain || a.details?.method) && (
                    <div className="text-xs text-ink-400 truncate">
                      {a.details?.url || a.details?.domain || a.details?.method}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-xs text-ink-400 whitespace-nowrap flex-none mt-1.5">
                {formatRelative(a.createdAt)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function OverviewTab({ domain }) {
  const { data, isLoading } = useQuery({
    queryKey: ['overview', domain._id],
    queryFn: () =>
      api.get(`/api/domains/${domain._id}/analytics/overview`).then((r) => r.data),
  });

  if (isLoading)
    return (
      <div className="space-y-6">
        <SkeletonHero />
        <SkeletonGrid count={4} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonCard lines={4} />
          <SkeletonCard lines={4} />
        </div>
      </div>
    );
  if (!data) return null;

  const scoreColor = data.audit
    ? data.audit.avgScore >= 75
      ? 'green'
      : data.audit.avgScore >= 50
        ? 'amber'
        : 'rose'
    : 'muted';

  return (
    <div className="space-y-6">
      {/* Hero banner */}
      <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
        <div className="absolute inset-0 opacity-30 bg-brand-radial" />
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
        <div className="absolute -bottom-32 -left-10 w-96 h-96 rounded-full bg-cyan-500 blur-3xl opacity-20" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Workspace
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold truncate">
              {domain.displayName || domain.domain}
            </h2>
            <div className="text-sm text-white/70 mt-1 truncate">{domain.domain}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {domain.verified ? (
              <span className="pill bg-emerald-500/20 text-emerald-200 backdrop-blur">
                <ShieldCheck className="w-3 h-3" /> Verified
              </span>
            ) : (
              <Link
                to="settings"
                className="pill bg-accent-amber/20 text-accent-amber hover:bg-accent-amber/30"
              >
                <ShieldAlert className="w-3 h-3" /> Verification needed
              </Link>
            )}
            <Link
              to="audit"
              className="inline-flex items-center gap-2 rounded-lg bg-white text-ink-900 px-4 py-2 text-sm font-semibold hover:bg-white/90 transition-colors"
            >
              <Rocket className="w-4 h-4" /> Run audit
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="AI referrals · 7d"
          value={data.aiReferrals.last7}
          hint={`30d: ${data.aiReferrals.last30}`}
          icon={TrendingUp}
          variant="brand"
          info={{
            title: 'AI referrals',
            body: 'Visitors who clicked through to your site from an AI engine (ChatGPT, Claude, Perplexity, Gemini, Copilot, Apple AI). Tagged by the tracking script in real time.',
          }}
        />
        <StatCard
          label="Bot visits · 30d"
          value={data.botActivity.last30Visits}
          hint={`${data.botActivity.last30Pages} pages crawled`}
          icon={Bot}
          variant="cyan"
          info={{
            title: 'Bot visits',
            body: 'Crawls from AI training and search bots (GPTBot, ClaudeBot, PerplexityBot, Google-Extended). The more they crawl, the more of your site AI engines can quote.',
          }}
        />
        <StatCard
          label="GEO score"
          value={data.audit ? `${data.audit.avgScore}/100` : '—'}
          hint={data.audit ? `Audited ${formatRelative(data.audit.lastRun)}` : 'No audit yet'}
          icon={Award}
          variant={scoreColor}
          info={{
            title: 'GEO score',
            body: 'A 0–100 grade of how AI-ready your site is across 7 pillars: schema, FAQs, Answer Capsules, heading hierarchy, bot access, llms.txt, and content quality. Run an audit to refresh.',
          }}
        />
        <StatCard
          label="Pages optimized"
          value={`${data.pages.optimized}/${data.pages.total || 0}`}
          hint="Schema + FAQs injected"
          icon={FileText}
          variant="amber"
          info={{
            title: 'Pages optimized',
            body: 'Pages we\'ve rewritten with FAQs, Answer Capsules, and JSON-LD schema. Original HTML is always recoverable from version history.',
          }}
        />
      </div>

      {data.aiReferrals.total === 0 && data.botActivity.last30Visits === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="No AI activity recorded yet"
          body={
            <>
              Install the tracking script on <strong>{domain.domain}</strong> to start
              capturing AI referrals and crawler visits. The snippet is in Settings →
              Tracking script.
            </>
          }
          action={
            <Link to="settings" className="btn-primary">
              <SettingsIcon className="w-4 h-4" /> View setup instructions
            </Link>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card">
              <div className="section-header">
                <div className="section-title">
                  <span className="section-icon">
                    <Rocket className="w-4 h-4" />
                  </span>
                  What to do next
                </div>
              </div>
              <ul className="space-y-3 text-sm text-ink-700">
                <NextStep
                  to="audit"
                  icon={ClipboardCheck}
                  title="Run an audit"
                  body="Score every page on AI-readiness and get prioritized fixes."
                />
                <NextStep
                  to="content"
                  icon={FileText}
                  title="Optimize top pages"
                  body="Rewrite your highest-traffic pages with AI — adds FAQs, Answer Capsules, and JSON-LD schema."
                />
                <NextStep
                  to="settings"
                  icon={SettingsIcon}
                  title="Generate /llms.txt"
                  body="Publish a guide for AI crawlers prioritizing your best content."
                />
              </ul>
            </div>
            <div className="card-dark relative overflow-hidden">
              <div className="absolute -bottom-20 -right-20 w-72 h-72 rounded-full bg-brand-500 blur-3xl opacity-30" />
              <div className="absolute inset-0 bg-grid opacity-20" />
              <div className="relative">
                <div className="section-title text-white">
                  <span className="section-icon bg-white/10 text-cyan-300">
                    <ShieldCheck className="w-4 h-4" />
                  </span>
                  Tracking status
                </div>
                <p className="text-sm text-white/70 leading-relaxed mt-3">
                  {domain.verified ? (
                    <>
                      Domain verified. Tracking script is active for{' '}
                      <strong className="text-white">{domain.domain}</strong>.
                    </>
                  ) : (
                    <>
                      Add the tracking script (Settings) and verify ownership to unlock the
                      full audit and{' '}
                      <code className="bg-white/10 rounded px-1 py-0.5">/llms.txt</code>{' '}
                      features.
                    </>
                  )}
                </p>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  {['ChatGPT', 'Claude', 'Perplexity', 'Gemini', 'Copilot', 'Apple AI'].map(
                    (label) => (
                      <DarkStat key={label} label={label} />
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
          <ActivityFeed domainId={domain._id} />
        </>
      )}
    </div>
  );
}

function NextStep({ to, title, body, icon: Icon }) {
  return (
    <li>
      <Link
        to={to}
        className="block rounded-xl border border-gray-100 p-3.5 hover:border-brand-200 hover:bg-brand-50/40 hover:shadow-sm transition-all"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {Icon && (
              <span className="flex-none h-8 w-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                <Icon className="w-4 h-4" />
              </span>
            )}
            <span className="font-semibold text-ink-900">{title}</span>
          </div>
          <ArrowRight className="w-4 h-4 text-brand-600 flex-none" />
        </div>
        <p className="text-xs text-ink-400 mt-2 leading-relaxed pl-11">{body}</p>
      </Link>
    </li>
  );
}

function DarkStat({ label }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 px-2 py-2 flex flex-col items-center gap-1">
      <CheckCircle2 className="w-4 h-4 text-emerald-300" />
      <div className="text-[10px] uppercase tracking-wide text-white/60">{label}</div>
    </div>
  );
}
